"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { supabase } from "../../lib/supabase";
import { User } from "@supabase/supabase-js";

interface AuthProps {
  user: User | null;
}

export function Auth({ user }: AuthProps) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      // Determinar URL de redirecionamento correta
      const getRedirectUrl = () => {
        const origin = window.location.origin;
        
        // Log para debug
        console.log('🔍 [DEBUG] Origin atual:', origin);
        
        // Se for ambiente de desenvolvimento, usar origin atual
        if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
          console.log('🔍 [DEBUG] Ambiente de desenvolvimento detectado');
          return `${origin}/planejamento`;
        }
        
        // Para produção, usar origin atual (já será a URL correta)
        console.log('🔍 [DEBUG] Ambiente de produção detectado');
        return `${origin}/planejamento`;
      };

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: getRedirectUrl(),
        },
      });

      if (error) {
        console.error('Erro no login com Google:', error);
        throw error;
      }

      // Redirecionamento será feito automaticamente pelo Supabase
    } catch (error) {
      console.error('Erro ao fazer login com Google:', error);
      alert('Erro ao fazer login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setLoading(true);
      await supabase.auth.signOut();
      router.push('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      setLoading(false);
    }
  };

  if (user) {
    // Usuário logado - exibe perfil
    return (
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          {user.user_metadata?.avatar_url && (
            <img
              src={user.user_metadata.avatar_url}
              alt="Foto de perfil"
              className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
            />
          )}
          <div className="hidden sm:block">
            <p className="text-sm font-medium text-slate-700">
              {user.user_metadata?.full_name || user.email?.split('@')[0] || 'Usuário'}
            </p>
            <p className="text-xs text-slate-500">
              {user.email}
            </p>
          </div>
        </div>
        
        <button
          onClick={handleSignOut}
          disabled={loading}
          className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saindo...' : 'Sair'}
        </button>
      </div>
    );
  }

  // Usuário não logado - exibe botão de login
  return (
    <button
      onClick={handleGoogleSignIn}
      disabled={loading}
      className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
    >
      <svg
        className="w-5 h-5"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#EA4335"
        />
      </svg>
      <span className="text-sm font-medium text-slate-700">
        {loading ? 'Entrando...' : 'Entrar com Google'}
      </span>
    </button>
  );
}
